import clsx from "clsx";
import { FC } from "react";

import { DisplayCaption } from "../DisplayCaption";

import { ConnectionStatusIndicator } from "./ConnectionStatusIndicator";
import { HomeButton } from "./HomeButton";
import { RecordingStatusIndicator } from "./RecordingStatusIndicator";
import { SectionSelect } from "./SectionSelect";
import { VoteRollup } from "./VoteRollup";

import { GlobalBanners } from "components/GlobalBanners";
import { LOGO_HEADER_SIZE } from "components/constants";
import { HelpCenterMenu } from "components/header/HelpCenterMenu";
import { ProfileSettingsMenu } from "components/header/ProfileSettingsMenu";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { SectionSettingsModal } from "components/modals/SectionSettingsModal";
import { t } from "i18n/i18n";
import { TrackedLink } from "mds/components/TrackedLink";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { ForumLogoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentClassUserIsInPersonClass, selectIsRecordingAgent } from "store/selectors";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useAv } from "utils/av";
import { useHomeUrl } from "utils/hooks/use-home-url";
import {
  useBorderedPresentationColoring,
  usePresentation,
  usePresentationCaption,
  useShouldShowPresentationCaption,
} from "utils/presentation";

// TODO: Potentially rename to SectionHeader
export const MainHeader: FC = () => {
  const isAssessing = useIsAssessing();
  const { isInVirtualClass } = useAv();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const isInVirtualClassOrObserving = isInVirtualClass || isObservingClass;
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isSmallScreen = !useIsMdOrLarger();
  const homeUrl = useHomeUrl();

  const { isPresenting } = usePresentation();

  const shouldShowPresentationCaption = useShouldShowPresentationCaption(true);
  const borderColorClass = useBorderedPresentationColoring();
  const { captionText, captionBgColor, linkText, linkUrl } = usePresentationCaption();

  const shouldShowVoteRollup = isInVirtualClassOrObserving || isInInPersonClass;

  // When presenting or assessing we intentionally avoid giving options for the user to leave the page view
  // without them choosing to formally end the presentation or assessment mode first. This is to reduce
  // potential confusion on how the system will handle when they navigate away.
  const enableNavigation = !isPresenting && !isAssessing;
  const disabledHomeButtonTooltipText = isPresenting
    ? t("main_header.disallowed_while_presenting")
    : t("main_header.disallowed_while_assessing");
  const restrictSectionSelect = isPresenting || isAssessing;
  const disabledSectionSelectTooltipText = isPresenting
    ? t("main_header.disallowed_while_presenting")
    : t("main_header.disallowed_while_assessing");

  const logo = (
    <ForumLogoIcon
      color={isInVirtualClassOrObserving ? "white" : "black"}
      width={LOGO_HEADER_SIZE}
    />
  );

  if (isSmallScreen) {
    console.warn("MainHeader should not be used for small screens.");
  }

  return (
    <>
      <GlobalBanners />
      <header
        className={clsx(
          "group/themable box-content flex h-9 items-center justify-between bg-white px-4 py-2 text-black-tint-20 data-[dark]:border-black-tint-15 data-[dark]:bg-black-tint-15 data-[dark]:text-white",
          shouldShowPresentationCaption
            ? `border-b-2 ${borderColorClass}`
            : "border-b border-black-tint-90",
        )}
        data-dark={isInVirtualClassOrObserving || undefined}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 text-black-tint-20">
          {enableNavigation ? (
            <TrackedLink
              aria-label={t("main_header.home")}
              className="flex flex-shrink-0 cursor-pointer items-center border-0 text-black"
              eventName="Logo - Homepage"
              target={isInVirtualClassOrObserving ? "_blank" : undefined}
              to={homeUrl}
            >
              {logo}
            </TrackedLink>
          ) : (
            <div className="flex items-center text-black">{logo}</div>
          )}

          <div className="border-l border-black-tint-90 py-[7px] group-data-[dark]/themable:border-black-tint-40" />

          <HomeButton
            disabled={!enableNavigation}
            disabledTooltipText={disabledHomeButtonTooltipText}
            openInNewTab={isInVirtualClassOrObserving}
          />

          <div className="flex min-w-0 flex-1 items-center gap-1">
            <SectionSelect
              disabled={restrictSectionSelect}
              disabledTooltipText={disabledSectionSelectTooltipText}
              openInNewTab={isInVirtualClassOrObserving}
            />
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 justify-self-end">
          {!isRecordingAgent && <ConnectionStatusIndicator className="flex items-center p-1" />}
          {!isRecordingAgent && isInVirtualClassOrObserving && <RecordingStatusIndicator />}
          {shouldShowVoteRollup && <VoteRollup />}
          {!isRecordingAgent && <HelpCenterMenu />}
          {!isRecordingAgent && <ProfileSettingsMenu />}
        </div>
      </header>

      {shouldShowPresentationCaption && (
        <DisplayCaption
          bgColorClass={captionBgColor}
          caption={captionText}
          linkText={linkText}
          linkUrl={linkUrl}
        />
      )}

      <SectionSettingsModal />
    </>
  );
};
